import React, { useState, useEffect } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import logo from "../components/assets/logo.png";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import SortableList, { SortableItem } from "../components/SortableList";
import { Select } from "antd";
import { useSettings } from "../hooks/useSettings";
import { InputSwitch } from "primereact/inputswitch";
import { usePageHeader } from "../hooks/usePageHeader";
import { Dialog } from "primereact/dialog";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSiteSettings } from "../hooks/useSite";
import { TabView, TabPanel } from "primereact/tabview";
import { useMe } from "../hooks/useMe";
import { useAuth } from "../hooks/useAuth";
import { useUsers } from "../hooks/useUsers";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import TinyMCEEditor from "../components/QuillEditor";
const FormInput = ({ id, name, label, type = "text", value, onChange }) => (
  <FloatLabel>
    <InputText
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      className="w-full border border-gray-500 p-1 rounded-md placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-gray-100 text-gray-600"
    />
    <label htmlFor={id} className="text-sm">
      {label}
    </label>
  </FloatLabel>
);

const PageHeaderSchema = Yup.object().shape({
  pageKey: Yup.string().required("Page key is required"),
  h1: Yup.string().required("H1 Title is required"),
  tagline: Yup.string().max(255, "Tagline must be 255 characters or less"),
});

// Replace ProfileSchema and add ChangePasswordSchema
const ProfileSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
});

const ChangePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .min(6, "Minimum 6 characters")
    .required("Current password is required"),
  newPassword: Yup.string()
    .min(6, "Minimum 6 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Confirm your new password"),
});

const Setting = () => {
  const {
    settings,
    isLoading: loading,
    updateSettings,
    isUpdating,
    isTogglingNavItem,
    isTogglingDropdown,
  } = useSettings();
  const { me, isLoadingMe } = useMe();
  const { updateProfile } = useUsers();
  const { changePassword } = useAuth();
  const {
    pageHeader,
    updatePageHeader,
    isLoading,
    isUpdating: updating,
  } = usePageHeader();
  const {
    data: siteSettings,
    isLoading: siteLoading,
    updateSiteMutation,
  } = useSiteSettings();

  const [selectedItems, setSelectedItems] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [clubName, setClubName] = useState("");
  const [clubSubtitle, setClubSubtitle] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [requireNMLS, setRequireNMLS] = useState(false);
  const [selectedPageKey, setSelectedPageKey] = useState("");
  const [showAddPageDialog, setShowAddPageDialog] = useState(false);
  const [editableH1, setEditableH1] = useState("");
  const [editableTagline, setEditableTagline] = useState("");
  const [editableContent, setEditableContent] = useState("");
  const [showDevBanner, setShowDevBanner] = useState(false);
  const [devBannerMessage, setDevBannerMessage] = useState("");
  const [devBannerChanged, setDevBannerChanged] = useState(false);

  // Tabs + Profile states
  const [activeIndex, setActiveIndex] = useState(0);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // password visibility toggles
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Track if we've initialized to prevent overwriting user edits
  const hasInitializedSettingsRef = React.useRef(false);
  const lastSyncedNavItemsRef = React.useRef(null);
  const isTogglingRef = React.useRef(false);
  const isSavingRef = React.useRef(false);

  useEffect(() => {
    if (settings && !hasInitializedSettingsRef.current) {
      setClubName(settings.clubName || "");
      setClubSubtitle(settings.clubSubtitle || "");
      setPhoneNumber(settings.phoneNumber || "");
      setEmail(settings.email || "");
      setRequireNMLS(settings.showPropertyQuestions || false);
      if (settings.navItems) {
        setSelectedItems(settings.navItems);
        lastSyncedNavItemsRef.current = JSON.stringify(settings.navItems);
      }
      hasInitializedSettingsRef.current = true;
    }
  }, [settings]);

  // Track when save completes to sync only after successful save
  const lastSaveStateRef = React.useRef(false);

  // Sync other fields only after successful save (when isUpdating goes from true to false)
  // Sync navItems after toggle operations complete (using isTogglingRef to prevent premature sync)
  useEffect(() => {
    if (settings && hasInitializedSettingsRef.current) {
      const wasUpdating = lastSaveStateRef.current;
      const isCurrentlyUpdating = isUpdating;
      const isCurrentlyToggling = isTogglingNavItem || isTogglingDropdown;

      // Sync non-navItem fields when save completes
      if (
        wasUpdating &&
        !isCurrentlyUpdating &&
        !isTogglingRef.current &&
        !isSavingRef.current
      ) {
        setClubName(settings.clubName || "");
        setClubSubtitle(settings.clubSubtitle || "");
        setPhoneNumber(settings.phoneNumber || "");
        setEmail(settings.email || "");
        setRequireNMLS(settings.showPropertyQuestions || false);
      }

      // Sync navItems when not actively toggling or saving
      // Use isTogglingRef to prevent sync during toggle operations (includes refetch period)
      if (
        settings.navItems &&
        !isSavingRef.current &&
        !isCurrentlyUpdating &&
        !isCurrentlyToggling &&
        !isTogglingRef.current
      ) {
        const serverNavItemsStr = JSON.stringify(settings.navItems);
        const currentNavItemsStr = JSON.stringify(selectedItems);

        // Sync from server to ensure toggle operations are reflected
        // Only sync if server data differs from current state
        if (serverNavItemsStr !== currentNavItemsStr) {
          setSelectedItems(settings.navItems);
          lastSyncedNavItemsRef.current = serverNavItemsStr;
        }
      }

      // Update ref for next render
      lastSaveStateRef.current = isCurrentlyUpdating;
    }
  }, [
    settings,
    isUpdating,
    selectedItems,
    isTogglingNavItem,
    isTogglingDropdown,
  ]); // Sync when save completes

  useEffect(() => {
    const hasNavItemsChanged =
      JSON.stringify(selectedItems) !== JSON.stringify(settings?.navItems);
    const hasLogoChanged = !!logoFile;
    const hasHeaderChanged =
      clubName !== settings?.clubName ||
      clubSubtitle !== settings?.clubSubtitle ||
      phoneNumber !== settings?.phoneNumber ||
      email !== settings?.email ||
      requireNMLS !== settings?.showPropertyQuestions;
    setHasChanges(hasNavItemsChanged || hasLogoChanged || hasHeaderChanged);
  }, [
    selectedItems,
    logoFile,
    clubName,
    clubSubtitle,
    phoneNumber,
    email,
    requireNMLS,
    settings,
  ]);

  useEffect(() => {
    if (selectedPageKey && Array.isArray(pageHeader)) {
      const selected = pageHeader.find(
        (header) => header.pageKey === selectedPageKey
      );
      setEditableH1(selected ? selected.h1 : "");
      setEditableTagline(selected ? selected.tagline || "" : "");
      setEditableContent(selected ? selected.content || "" : "");
    }
  }, [selectedPageKey, pageHeader]);

  // Track if we've done initial sync to prevent overwriting user edits
  const hasInitializedRef = React.useRef(false);

  // Sync from siteSettings only on initial load
  useEffect(() => {
    if (siteSettings && !hasInitializedRef.current) {
      setShowDevBanner(Boolean(siteSettings.showDevBanner));
      setDevBannerMessage(siteSettings.devBannerMessage || "");
      hasInitializedRef.current = true;
    }
  }, [siteSettings]);

  // Sync after successful save
  useEffect(() => {
    if (updateSiteMutation.isSuccess && siteSettings) {
      setShowDevBanner(Boolean(siteSettings.showDevBanner));
      setDevBannerMessage(siteSettings.devBannerMessage || "");
    }
  }, [updateSiteMutation.isSuccess, siteSettings]);

  useEffect(() => {
    if (siteSettings) {
      const bannerChanged =
        showDevBanner !== Boolean(siteSettings.showDevBanner);
      const messageChanged =
        devBannerMessage.trim() !==
        (siteSettings.devBannerMessage || "").trim();
      setDevBannerChanged(bannerChanged || messageChanged);
    } else {
      // If no siteSettings, check if values differ from defaults
      setDevBannerChanged(
        showDevBanner !== false || devBannerMessage.trim() !== ""
      );
    }
  }, [showDevBanner, devBannerMessage, siteSettings]);

  const handleDevBannerSave = () => {
    updateSiteMutation.mutate({ showDevBanner, devBannerMessage });
  };

  const handleSelectChange = (values) => {
    const filtered = values
      .map((val) => settings.navItems.find((item) => item.label === val))
      .filter(Boolean);
    setSelectedItems(filtered);
  };

  const handleOrderChange = (newOrder) => {
    setSelectedItems(newOrder);
  };

  const handleSave = () => {
    // Mark that we're saving to prevent sync from overwriting
    isSavingRef.current = true;

    // Update the ref BEFORE saving so we know what we're saving
    const navItemsToSave = JSON.stringify(selectedItems);
    lastSyncedNavItemsRef.current = navItemsToSave;

    const data = {
      clubName,
      clubSubtitle,
      phoneNumber,
      email,
      showPropertyQuestions: requireNMLS,
      navItems: selectedItems.map((item, index) => ({
        id: item.id,
        label: item.label,
        path: item.path,
        type: item.type,
        position: index,
        dropdownItems: (item.dropdownItems || []).map(
          (dropdownItem, dropdownIndex) => ({
            id: dropdownItem.id,
            label: dropdownItem.label,
            path: dropdownItem.path,
            position: dropdownIndex,
          })
        ),
      })),
    };

    updateSettings({ data, logoFile });

    // Reset saving flag after a delay to allow save to complete
    // The isUpdating flag will handle the actual completion, but we need this
    // to prevent sync during the refetch that happens immediately after save
    setTimeout(() => {
      isSavingRef.current = false;
    }, 2000);
  };

  if (loading || isLoading || siteLoading || isLoadingMe)
    return (
      <div className="flex h-screen justify-center items-center">
        <i
          className="pi pi-spin pi-spinner text-red-700"
          style={{ fontSize: "2rem" }}
        ></i>
      </div>
    );

  return (
    <div className="p-5">
      <div className="mb-5 border-b-2 border-gray-300 py-4">
        <h1 className="text-base sm:text-lg lg:text-xl text-[#003049] font-bold">
          Settings
        </h1>
      </div>

      {/* TABS */}
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
        panelContainerClassName="p-4 bg-white rounded-b-lg shadow"
      >
        <TabPanel
          header="App settings"
          headerClassName="text-gray-700"
          headerTemplate={(options) => (
            <button
              type="button"
              onClick={options.onClick}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                options.selected
                  ? "border-[#003049] text-[#003049]"
                  : "border-transparent text-gray-500 hover:text-[#003049] hover:border-blue-300"
              } focus:outline-none`}
            >
              {options.titleElement}
            </button>
          )}
        >
          {/* Move your existing settings UI into this tab */}
          <section className="space-y-5 lg:space-y-10 w-full border-b-2 border-gray-300 pb-5 mb-4">
            {/* Website Logo Section */}
            <div className="grid lg:grid-cols-2 w-full mb-5 border-b-2 border-gray-300 py-4">
              <div>
                <h2 className="font-[500px]">Website logo</h2>
                <p className="text-sm text-gray-500">
                  Update your website logo.
                </p>
              </div>
              <div className="flex items-center gap-10">
                <div>
                  <img
                    src={
                      logoFile
                        ? URL.createObjectURL(logoFile)
                        : settings.logoUrl || settings.logoPath || logo
                    }
                    alt="website logo"
                    className="max-w-[120px] max-h-20 w-auto h-auto rounded-md border object-contain"
                  />
                </div>
                <div className="flex items-center gap-5">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files[0])}
                  />
                </div>
              </div>
            </div>

            {/* Website Header Section */}
            <div className="grid lg:grid-cols-2 w-full mb-5 border-b-2 border-gray-300 py-4">
              <div>
                <h2 className="font-[500px]">Website header</h2>
                <p className="text-sm text-gray-500">
                  Update your website header.
                </p>
              </div>
              <div className="flex items-center gap-10">
                <form className="px-2 py-4 border">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="py-2">
                      <FormInput
                        id="clubName"
                        name="clubName"
                        label="Club name"
                        value={clubName}
                        onChange={(e) => setClubName(e.target.value)}
                      />
                    </div>
                    <div className="py-2">
                      <FormInput
                        id="clubSubtitle"
                        name="clubSubtitle"
                        label="Club subtitle"
                        value={clubSubtitle}
                        onChange={(e) => setClubSubtitle(e.target.value)}
                      />
                    </div>

                    <div className="py-2">
                      <FormInput
                        id="phoneNumber"
                        name="phoneNumber"
                        label="Phone number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                    </div>
                    <div className="py-2">
                      <FormInput
                        id="email"
                        name="email"
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* MLS Number Section */}
            <div className="grid lg:grid-cols-2 w-full mb-5 border-b-2 border-gray-300 py-4">
              <div>
                <h2 className="font-[500px]">MLS number requirement</h2>
                <p className="text-sm text-gray-500">
                  Toggle whether to require MLS number when creating properties.
                </p>
              </div>
              <div className="flex items-center gap-10">
                <div className="flex items-center gap-2">
                  <InputSwitch
                    checked={Boolean(requireNMLS)}
                    onChange={(e) => {
                      const newValue = Boolean(e.value);
                      setRequireNMLS(newValue);
                    }}
                  />
                  <span className="text-sm text-gray-600">
                    {requireNMLS ? "Required" : "Not Required"}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation Dropdown Section */}
            <div className="grid lg:grid-cols-2 w-full mb-5 py-4">
              <div>
                <h2 className="text-lg font-semibold">Navigation dropdown</h2>
                <p className="text-sm text-gray-500">
                  Select items and drag to reorder them. Add dropdown items
                  where needed.
                </p>
              </div>
              <div>
                <SortableList
                  items={selectedItems.map((item) => ({
                    ...item,
                    active: item.status,
                    dropdownItems: (item.dropdownItems || [])
                      .sort((a, b) => a.position - b.position)
                      .map((dropdown) => ({
                        ...dropdown,
                        active: dropdown.status,
                      })),
                  }))}
                  onChange={handleOrderChange}
                  onToggle={(itemId, checked) => {
                    isTogglingRef.current = true;
                    const updatedItems = selectedItems.map((item) =>
                      item.id === itemId ? { ...item, status: checked } : item
                    );
                    setSelectedItems(updatedItems);
                    // Reset toggle flag after a delay to allow API call and refetch to complete
                    // Refetch happens after 500ms, so wait 1500ms to ensure server state is synced
                    setTimeout(() => {
                      isTogglingRef.current = false;
                    }, 1500);
                  }}
                  onDropdownToggle={(dropdownId, checked) => {
                    isTogglingRef.current = true;
                    const updatedItems = selectedItems.map((item) => ({
                      ...item,
                      dropdownItems: (item.dropdownItems || []).map(
                        (dropdown) =>
                          dropdown.id === dropdownId
                            ? { ...dropdown, status: checked }
                            : dropdown
                      ),
                    }));
                    setSelectedItems(updatedItems);
                    // Reset toggle flag after a delay to allow API call and refetch to complete
                    // Refetch happens after 500ms, so wait 1500ms to ensure server state is synced
                    setTimeout(() => {
                      isTogglingRef.current = false;
                    }, 1500);
                  }}
                  renderItem={(item) => (
                    <SortableItem
                      active={item.active}
                      key={item.id}
                      id={item.id}
                      label={item.label}
                      dropdownItems={item.dropdownItems.map((dropdown) => ({
                        ...dropdown,
                        active: dropdown.status,
                      }))}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end items-center gap-5 mt-5">
              <button
                className={`px-4 py-2 text-sm sm:text-base rounded-md text-white font-semibold ${
                  hasChanges ? "bg-[#BA1500]" : "bg-gray-400 cursor-not-allowed"
                }`}
                onClick={handleSave}
                disabled={!hasChanges || isUpdating}
              >
                {isUpdating ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </section>

          {/* Site Settings */}
          <section className="space-y-5 lg:space-y-10 w-full border-b-2 border-gray-300 pb-5 mb-4">
            <div className="grid lg:grid-cols-2 w-full mb-5 py-2">
              <div>
                <h2 className="font-semibold text-base sm:text-lg">
                  Site settings
                </h2>
                <p className="text-sm text-gray-500">
                  Manage site-wide settings such as the development banner.
                </p>
              </div>
              <div className="flex flex-col gap-4 w-full">
                <div className="flex items-center gap-4">
                  <span className="font-medium">Dev banner</span>
                  <InputSwitch
                    checked={Boolean(showDevBanner)}
                    onChange={(e) => {
                      const newValue = Boolean(e.value);
                      setShowDevBanner(newValue);
                    }}
                  />
                  <span className="text-sm text-gray-600">
                    {showDevBanner ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    htmlFor="devBannerMessage"
                  >
                    Dev banner message
                  </label>
                  <input
                    id="devBannerMessage"
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                    value={devBannerMessage}
                    onChange={(e) => setDevBannerMessage(e.target.value)}
                    disabled={!showDevBanner}
                    placeholder="Enter a message to display in the dev banner"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    className={`px-4 py-2 text-sm sm:text-base rounded-md text-white font-semibold ${
                      devBannerChanged
                        ? "bg-[#BA1500]"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                    onClick={handleDevBannerSave}
                    disabled={!devBannerChanged || updateSiteMutation.isLoading}
                  >
                    {updateSiteMutation.isPending
                      ? "Saving..."
                      : "Save Dev Banner"}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Page Header */}
          <section className="space-y-5 lg:space-y-10 w-full border-b-2 border-gray-300 pb-5 mb-4">
            <div>
              <h2 className="font-[500px]">Page headers</h2>
              <p className="text-sm text-gray-500">
                Manage H1 titles and taglines for each page. Select a page to
                edit its content.
              </p>
            </div>

            {/* Add New Page Header Button */}
            <div className="flex justify-between items-center">
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                onClick={() => setShowAddPageDialog(true)}
              >
                Add New Page Header
              </button>
            </div>

            {/* Page Selection and Editing */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Page Selection */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700">
                  Select page to edit
                </h3>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Choose a page to edit"
                  value={selectedPageKey || undefined}
                  onChange={setSelectedPageKey}
                  size="large"
                >
                  {Array.isArray(pageHeader) &&
                    pageHeader.map((header) => (
                      <Select.Option
                        key={header.pageKey}
                        value={header.pageKey}
                      >
                        {header.pageKey.charAt(0).toUpperCase() +
                          header.pageKey.slice(1)}
                      </Select.Option>
                    ))}
                </Select>

                {/* Available Pages List */}
                <div className="mt-4">
                  <h4 className="font-medium text-gray-600 mb-2">
                    Available pages:
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {(() => {
                      const allPages = [
                        "home",
                        "docs",
                        "events",
                        "team",
                        "mapsearch",
                        "contact",
                        "about-us",
                        "eric-nur",
                        "floor-plan",
                        "floor-plans",
                        "rules-and-regulations",
                        "board-members",
                        "blog",
                        "thank-you",
                        "privacy-policy",
                        "terms-and-conditions",
                        "community",
                      ];

                      return allPages.map((page) => {
                        const hasHeader = pageHeader?.some(
                          (h) => h.pageKey === page
                        );
                        return (
                          <div
                            key={page}
                            className={`p-2 rounded border text-xs ${
                              hasHeader
                                ? "border-green-300 bg-green-50 text-green-700"
                                : "border-gray-200 bg-gray-50 text-gray-500"
                            }`}
                          >
                            {page
                              .replace(/-/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                            {hasHeader && <span className="ml-1">✓</span>}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              {/* Page Content Editing */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700">
                  Edit page content
                </h3>
                {selectedPageKey ? (
                  <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Page:{" "}
                        <span className="font-semibold capitalize">
                          {selectedPageKey.replace(/-/g, " ")}
                        </span>
                      </label>
                    </div>

                    {/* H1 Title */}
                    <div>
                      <FormInput
                        id={`h1-${selectedPageKey}`}
                        name={`h1-${selectedPageKey}`}
                        label="H1 title"
                        value={editableH1}
                        onChange={(e) => setEditableH1(e.target.value)}
                      />
                    </div>

                    {/* Tagline */}
                    <div>
                      <FormInput
                        id={`tagline-${selectedPageKey}`}
                        name={`tagline-${selectedPageKey}`}
                        label="Tagline (optional)"
                        value={editableTagline}
                        onChange={(e) => setEditableTagline(e.target.value)}
                      />
                    </div>

                    {/* Content Editor */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Page content (HTML)
                      </label>
                      <TinyMCEEditor
                        value={editableContent}
                        onChange={(content) => setEditableContent(content)}
                        height={300}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Full page content in HTML format. This will be displayed
                        on the page.
                      </p>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                      <button
                        className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                        onClick={() => {
                          const header = pageHeader.find(
                            (h) => h.pageKey === selectedPageKey
                          );
                          if (
                            header &&
                            (editableH1 !== header.h1 ||
                              editableTagline !== (header.tagline || "") ||
                              editableContent !== (header.content || ""))
                          ) {
                            updatePageHeader({
                              key: selectedPageKey,
                              h1: editableH1,
                              tagline: editableTagline,
                              content: editableContent,
                            });
                          }
                        }}
                        disabled={
                          !selectedPageKey ||
                          !editableH1.trim() ||
                          (pageHeader.find((h) => h.pageKey === selectedPageKey)
                            ?.h1 === editableH1 &&
                            pageHeader.find(
                              (h) => h.pageKey === selectedPageKey
                            )?.tagline === editableTagline &&
                            pageHeader.find(
                              (h) => h.pageKey === selectedPageKey
                            )?.content === editableContent) ||
                          updating
                        }
                      >
                        {updating ? "Updating..." : "Save Changes"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    <p>Select a page from the dropdown to edit its content</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Dialog stays inside this tab */}
          <Dialog
            header="Add page header"
            visible={showAddPageDialog}
            style={{ width: "90vw", maxWidth: "500px" }}
            onHide={() => setShowAddPageDialog(false)}
            modal
          >
            <Formik
              initialValues={{ pageKey: "", h1: "", tagline: "", content: "" }}
              validationSchema={PageHeaderSchema}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                setSubmitting(true);
                try {
                  updatePageHeader({
                    key: values.pageKey.trim(),
                    h1: values.h1.trim(),
                    tagline: values.tagline.trim(),
                    content: values.content || "",
                  });
                  resetForm();
                  setShowAddPageDialog(false);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting, errors, touched, setFieldValue, values }) => (
                <Form className="flex flex-col gap-4 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select page
                    </label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Choose a page to add header for"
                      value={values.pageKey}
                      onChange={(value) => setFieldValue("pageKey", value)}
                      size="large"
                      dropdownStyle={{ zIndex: 9999 }}
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                    >
                      {(() => {
                        const allPages = [
                          "home",
                          "docs",
                          "events",
                          "team",
                          "mapsearch",
                          "contact",
                          "about-us",
                          "eric-nur",
                          "floor-plans",
                          "rules-and-regulations",
                          "board-members",
                          "blog",
                          "thank-you",
                        ];

                        const filteredPages = allPages.filter((page) => {
                          const hasHeader = pageHeader?.some(
                            (h) => h.pageKey === page
                          );
                          return !hasHeader;
                        });

                        // Debug: Log each option being created
                        const options = filteredPages.map((page) => {
                          const option = (
                            <Select.Option key={page} value={page}>
                              {page
                                .replace(/-/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </Select.Option>
                          );
                          return option;
                        });

                        return options;
                      })()}
                    </Select>

                    {/* Debug: Simple test dropdown */}
                    {/* <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded">
                      <p className="text-xs text-yellow-800 mb-1">DEBUG: Test Dropdown</p>
                      <select className="w-full p-1 text-xs border rounded">
                        <option value="">-- Select Test --</option>
                        <option value="test1">Test Option 1</option>
                        <option value="test2">Test Option 2</option>
                      </select>
                    </div> */}

                    {errors.pageKey && touched.pageKey && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.pageKey}
                      </div>
                    )}
                    {values.pageKey &&
                      pageHeader?.some((h) => h.pageKey === values.pageKey) && (
                        <div className="text-orange-600 text-xs mt-1">
                          ⚠️ This page already has a header. Please select a
                          different page.
                        </div>
                      )}
                  </div>
                  <div>
                    <Field
                      name="h1"
                      as={InputText}
                      placeholder="H1 title"
                      className="w-full bg-gray-50 px-2 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-500"
                    />
                    {errors.h1 && touched.h1 && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.h1}
                      </div>
                    )}
                  </div>
                  <div>
                    <Field
                      name="tagline"
                      as={InputText}
                      placeholder="Tagline (optional)"
                      className="w-full bg-gray-50 px-2 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-500"
                    />
                    {errors.tagline && touched.tagline && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.tagline}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Page content (HTML) - optional
                    </label>
                    <TinyMCEEditor
                      value={values.content || ""}
                      onChange={(content) => setFieldValue("content", content)}
                      height={250}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Full page content in HTML format.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAddPageDialog(false)}
                      className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      disabled={
                        isSubmitting ||
                        !values.pageKey ||
                        pageHeader?.some((h) => h.pageKey === values.pageKey)
                      }
                    >
                      {isSubmitting ? "Creating..." : "Create Page Header"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </Dialog>
        </TabPanel>

        <TabPanel
          header="My profile"
          headerClassName="text-gray-700"
          headerTemplate={(options) => (
            <button
              type="button"
              onClick={options.onClick}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                options.selected
                  ? "border-[#003049] text-[#003049]"
                  : "border-transparent text-gray-500 hover:text-[#003049] hover:border-blue-300"
              } focus:outline-none`}
            >
              {options.titleElement}
            </button>
          )}
        >
          {/* Formik form without password */}
          <Formik
            enableReinitialize
            initialValues={{
              name: me?.name || "",
              email: me?.email || "",
            }}
            validationSchema={ProfileSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setSavingProfile(true);
                const payload = {
                  name: values.name.trim(),
                  email: values.email.trim(),
                };
                await updateProfile(payload);
              } finally {
                setSavingProfile(false);
                setSubmitting(false);
              }
            }}
          >
            {({ values, handleChange, errors, touched, isSubmitting }) => (
              <Form
                className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2"
                autoComplete="off"
              >
                <div className="col-span-1">
                  <FormInput
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter your name"
                    onChange={handleChange}
                    value={values.name}
                    label={"Name"}
                  />
                  {touched.name && errors.name && (
                    <div className="text-xs text-red-600 mt-1">
                      {errors.name}
                    </div>
                  )}
                </div>

                <div className="col-span-1">
                  <FormInput
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    onChange={handleChange}
                    value={values.email}
                    label={"Email"}
                  />
                  {touched.email && errors.email && (
                    <div className="text-xs text-red-600 mt-1">
                      {errors.email}
                    </div>
                  )}
                </div>

                <div className="col-span-1 md:col-span-2 mt-2 flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-md bg-[#BA1500] text-white font-semibold"
                    disabled={savingProfile || isSubmitting}
                  >
                    {savingProfile || isSubmitting
                      ? "Saving..."
                      : "Save Profile"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </TabPanel>

        {/* NEW: Change Password tab */}
        <TabPanel
          header="Change password"
          headerClassName="text-gray-700"
          headerTemplate={(options) => (
            <button
              type="button"
              onClick={options.onClick}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                options.selected
                  ? "border-[#003049] text-[#003049]"
                  : "border-transparent text-gray-500 hover:text-[#003049] hover:border-blue-300"
              } focus:outline-none`}
            >
              {options.titleElement}
            </button>
          )}
        >
          <Formik
            initialValues={{
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            }}
            validationSchema={ChangePasswordSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                await changePassword({
                  currentPassword: values.currentPassword,
                  newPassword: values.newPassword,
                });
                resetForm();
              } catch (error) {
                // Error is already handled by react-query's onError in useAuth.js,
                // so we just prevent it from propagating further to the console.
                // console.error("Password change submission error:", error); // Keep for debugging if needed, but won't show as unhandled
              } finally {
                setSavingPassword(false);
                setSubmitting(false);
              }
            }}
          >
            {({ values, handleChange, errors, touched, isSubmitting }) => (
              <Form
                className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2"
                autoComplete="off"
              >
                <div className="col-span-1 md:col-span-2">
                  <div className="relative">
                    <FloatLabel>
                      <InputText
                        id="currentPassword"
                        name="currentPassword"
                        type={showCurrent ? "text" : "password"}
                        value={values.currentPassword}
                        onChange={handleChange}
                        className="w-full"
                        autoComplete="current-password"
                        autoCapitalize="none"
                        spellCheck={false}
                      />
                      <label htmlFor="currentPassword" className="text-sm">
                        Current password
                      </label>
                    </FloatLabel>
                    <button
                      type="button"
                      aria-label="Toggle current password visibility"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                      onClick={() => setShowCurrent((s) => !s)}
                    >
                      {showCurrent ? (
                        <AiOutlineEyeInvisible size={18} />
                      ) : (
                        <AiOutlineEye size={18} />
                      )}
                    </button>
                  </div>
                  {touched.currentPassword && errors.currentPassword && (
                    <div className="text-xs text-red-600 mt-1">
                      {errors.currentPassword}
                    </div>
                  )}
                </div>

                <div className="col-span-1">
                  <div className="relative">
                    <FloatLabel>
                      <InputText
                        id="newPassword"
                        name="newPassword"
                        type={showNew ? "text" : "password"}
                        value={values.newPassword}
                        onChange={handleChange}
                        className="w-full"
                        autoComplete="new-password"
                        autoCapitalize="none"
                        spellCheck={false}
                      />
                      <label htmlFor="newPassword" className="text-sm">
                        New password
                      </label>
                    </FloatLabel>
                    <button
                      type="button"
                      aria-label="Toggle new password visibility"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                      onClick={() => setShowNew((s) => !s)}
                    >
                      {showNew ? (
                        <AiOutlineEyeInvisible size={18} />
                      ) : (
                        <AiOutlineEye size={18} />
                      )}
                    </button>
                  </div>
                  {touched.newPassword && errors.newPassword && (
                    <div className="text-xs text-red-600 mt-1">
                      {errors.newPassword}
                    </div>
                  )}
                </div>

                <div className="col-span-1">
                  <div className="relative">
                    <FloatLabel>
                      <InputText
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirm ? "text" : "password"}
                        value={values.confirmPassword}
                        onChange={handleChange}
                        className="w-full"
                        autoComplete="new-password"
                        autoCapitalize="none"
                        spellCheck={false}
                      />
                      <label htmlFor="confirmPassword" className="text-sm">
                        Confirm new password
                      </label>
                    </FloatLabel>
                    <button
                      type="button"
                      aria-label="Toggle confirm password visibility"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                      onClick={() => setShowConfirm((s) => !s)}
                    >
                      {showConfirm ? (
                        <AiOutlineEyeInvisible size={18} />
                      ) : (
                        <AiOutlineEye size={18} />
                      )}
                    </button>
                  </div>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <div className="text-xs text-red-600 mt-1">
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>

                <div className="col-span-1 md:col-span-2 mt-2 flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-md bg-[#BA1500] text-white font-semibold"
                    disabled={savingPassword || isSubmitting}
                  >
                    {savingPassword || isSubmitting
                      ? "Updating..."
                      : "Update Password"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default Setting;
